// Bounded A01 projection validation and analytic reference resolution; no DOM/backend dependency.
const fail = message => { throw new TypeError(`Neutral projection: ${message}`); };
const check = (value, message) => { if (!value) fail(message); };
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const text = value => typeof value === 'string' && value.length > 0 && value.length <= 256;
const number = value => typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 1e9;
const tuple = (value, length) => Array.isArray(value) && value.length === length && value.every(number);
const keys = (value, allowed, label) => check(object(value) && Object.keys(value).every(key => allowed.includes(key)), `${label} fields`);
const sameIdentity = (a, b) => a && b && ['assetId', 'revisionId', 'variantId'].every(key => a[key] === b[key]);

export function validateNeutralProjection(value, { allowHiddenEdges = false, allowComplexRegions = false } = {}) {
  keys(value, ['contract', 'contractVersion', 'identity', 'occurrenceId', 'view', 'bounds', 'hiddenEdgeMode', 'approximation', 'curves', 'regions'], 'document');
  check(value.contract === 'ks.asset-projection' && value.contractVersion === 1, 'unsupported contract/version');
  keys(value.identity, ['assetId', 'revisionId', 'variantId'], 'identity');
  check(['assetId', 'revisionId', 'variantId'].every(key => text(value.identity[key])), 'complete variant identity required');
  check(text(value.occurrenceId), 'occurrenceId');
  keys(value.view, ['viewId', 'originMm', 'right', 'up', 'towardViewer'], 'view');
  check(text(value.view.viewId) && ['originMm', 'right', 'up', 'towardViewer'].every(key => tuple(value.view[key], 3)), 'view basis');
  const [r, u, n] = [value.view.right, value.view.up, value.view.towardViewer];
  const dot = (a, b) => a.reduce((sum, coordinate, i) => sum + coordinate * b[i], 0);
  const cross = [r[1] * u[2] - r[2] * u[1], r[2] * u[0] - r[0] * u[2], r[0] * u[1] - r[1] * u[0]];
  check([r, u, n].every(axis => Math.abs(dot(axis, axis) - 1) <= 1e-6)
    && Math.abs(dot(r, u)) <= 1e-6 && cross.every((v, i) => Math.abs(v - n[i]) <= 1e-6), 'orthonormal right-handed view required');
  keys(value.bounds, ['min', 'max'], 'bounds');
  for (const bound of [value.bounds.min, value.bounds.max]) {
    keys(bound, ['x', 'y'], 'bound'); check(number(bound.x) && number(bound.y), 'finite bounds');
  }
  check(value.bounds.max.x > value.bounds.min.x && value.bounds.max.y > value.bounds.min.y, 'positive bounds');
  check(value.hiddenEdgeMode === 'none' || (allowHiddenEdges === true && value.hiddenEdgeMode === 'dashed'),
    'hidden-edge styling requires explicit consumer support');
  keys(value.approximation, ['kind', 'scope'], 'approximation');
  check(value.approximation.kind === 'sampled-unverified' && value.approximation.scope === 'display-points-and-region-rings', 'explicit sampled approximation required');
  check(Array.isArray(value.curves) && value.curves.length <= 5000 && Array.isArray(value.regions) && value.regions.length <= (allowComplexRegions === true ? 20000 : 2000), 'geometry count limit');
  let pointCount = 0;
  const points = (list, minimum, withinBounds = false) => {
    check(Array.isArray(list) && list.length >= minimum && list.length <= 8192 && list.every(point => tuple(point, 2)), 'finite point list');
    pointCount += list.length; check(pointCount <= 200000, 'total point budget exceeded');
    if (withinBounds) {
      const tolerance = 1e-6 + 1e-12 * Math.max(...Object.values(value.bounds.min).map(Math.abs), ...Object.values(value.bounds.max).map(Math.abs));
      check(list.every(point => point.every((coordinate, i) => {
        const axis = i === 0 ? 'x' : 'y';
        return coordinate >= value.bounds.min[axis] - tolerance && coordinate <= value.bounds.max[axis] + tolerance;
      })), 'display geometry exceeds bounds');
    }
  };
  const curveIds = new Set();
  for (const curve of value.curves) {
    keys(curve, ['id', 'kind', 'analytic', 'displayPoints', 'closed', 'visibility', 'category', 'parameterRange', 'sourceSupportId', 'featureRef'], 'curve');
    check(text(curve.id) && !curveIds.has(curve.id), 'duplicate/invalid curve id'); curveIds.add(curve.id);
    check(typeof curve.closed === 'boolean' && (curve.visibility === 'visible'
      || (value.hiddenEdgeMode === 'dashed' && curve.visibility === 'hidden'))
      && [null, 'sharp', 'outline', 'rg1', 'rgn', 'iso'].includes(curve.category), 'curve presentation');
    check(curve.sourceSupportId === null || text(curve.sourceSupportId), 'source support id');
    check(curve.parameterRange === null || (tuple(curve.parameterRange, 2) && curve.parameterRange[1] > curve.parameterRange[0]), 'native parameter range');
    points(curve.displayPoints, 2, true);
    const ref = curve.featureRef;
    keys(ref, ['identity', 'occurrenceId', 'featureId', 'scope', 'viewId'], 'featureRef');
    check(sameIdentity(ref.identity, value.identity) && ref.occurrenceId === value.occurrenceId && ref.featureId === curve.id
      && ref.scope === 'view' && ref.viewId === value.view.viewId, 'feature binding');
    const a = curve.analytic;
    if (curve.kind === 'line') {
      keys(a, ['start', 'end'], 'line analytic'); check(tuple(a.start, 2) && tuple(a.end, 2), 'line endpoints');
    } else if (['circle', 'circular-arc', 'ellipse', 'elliptic-arc'].includes(curve.kind)) {
      const circular = ['circle', 'circular-arc'].includes(curve.kind);
      keys(a, circular ? ['center', 'radius', 'startAngle', 'endAngle', 'xAxisAngleRad', 'normalZ']
        : ['center', 'majorRadius', 'minorRadius', 'rotationRad', 'startAngle', 'endAngle', 'normalZ'], 'conic analytic');
      check(tuple(a.center, 2) && number(a.startAngle) && number(a.endAngle) && a.endAngle > a.startAngle
        && number(a.normalZ) && Math.abs(Math.abs(a.normalZ) - 1) <= 1e-6, 'conic frame/domain');
      check(circular ? number(a.radius) && a.radius > 0 && number(a.xAxisAngleRad)
        : number(a.majorRadius) && a.majorRadius > 0 && number(a.minorRadius) && a.minorRadius > 0 && number(a.rotationRad), 'conic radii/axis');
    } else if (curve.kind === 'bspline') {
      keys(a, ['degree', 'poles', 'weights', 'knots', 'multiplicities', 'startAngle'], 'bspline analytic');
      points(a.poles, 2);
      check(Number.isInteger(a.degree) && a.degree >= 1 && a.degree <= 32 && a.startAngle === null, 'bspline degree');
      check(Array.isArray(a.knots) && a.knots.length >= 2 && a.knots.length <= 8192 && a.knots.every(number)
        && a.knots.every((v, i) => i === 0 || v > a.knots[i - 1]), 'bspline knots');
      check(Array.isArray(a.multiplicities) && a.multiplicities.length === a.knots.length
        && a.multiplicities.every(v => Number.isInteger(v) && v > 0 && v <= 33), 'bspline multiplicities');
      check(a.weights === null || (Array.isArray(a.weights) && a.weights.length === a.poles.length && a.weights.every(v => number(v) && v > 0)), 'bspline weights');
    } else if (curve.kind === 'polyline') keys(a, [], 'polyline analytic');
    else fail(`unsupported curve kind ${String(curve.kind)}`);
  }
  const regionIds = new Set();
  for (const region of value.regions) {
    keys(region, ['id', 'outer', 'holes', 'color'], 'region');
    check(text(region.id) && !regionIds.has(region.id), 'duplicate/invalid region id'); regionIds.add(region.id);
    const ring = list => {
      points(list, 4, true);
      check(list[0].every((coordinate, i) => coordinate === list.at(-1)[i])
        && new Set(list.slice(0, -1).map(point => point.join(','))).size >= 3, 'closed region ring required');
    };
    ring(region.outer);
    check(Array.isArray(region.holes) && region.holes.length <= 1000, 'hole count');
    for (const hole of region.holes) ring(hole);
    check(tuple(region.color, 3) && region.color.every(v => v >= 0 && v <= 1), 'region RGB range');
  }
  check(value.curves.length + value.regions.length > 0, 'empty projection');
  return value;
}

/** Resolve only this view's analytic primitives. No model or fallback promotion. */
export function resolveProjectionReference(projection, reference, selector) {
  validateNeutralProjection(projection);
  const unresolved = reason => ({ status: 'unresolved', reason });
  if (!reference || !sameIdentity(reference.identity, projection.identity) || reference.occurrenceId !== projection.occurrenceId
    || reference.scope !== 'view' || reference.viewId !== projection.view.viewId) return unresolved('identity-or-view-mismatch');
  const curve = projection.curves.find(candidate => candidate.id === reference.featureId);
  if (!curve) return unresolved('feature-missing');
  const selectorKeys = { point: ['kind', 'point'], center: ['kind'], radius: ['kind'], 'curve-parameter': ['kind', 'parameterization', 'value'] };
  if (!object(selector) || !Object.hasOwn(selectorKeys, selector.kind)
    || !Object.keys(selector).every(key => selectorKeys[selector.kind].includes(key))) return unresolved('unsupported-selector-fields');
  const a = curve.analytic;
  const point = coordinates => ({ status: 'resolved', authority: 'analytic-view', value: { kind: 'point', point: coordinates } });
  const circular = ['circle', 'circular-arc'].includes(curve.kind);
  if (selector?.kind === 'center' && circular) return point([...a.center]);
  if (selector?.kind === 'radius' && circular) return { status: 'resolved', authority: 'analytic-view', value: { kind: 'radius', radiusMm: a.radius } };
  if (selector?.kind === 'point' && curve.kind === 'line' && ['start', 'end'].includes(selector.point)) return point([...a[selector.point]]);
  if (selector?.kind === 'curve-parameter' && selector.parameterization === 'native' && number(selector.value) && curve.parameterRange) {
    const [start, end] = curve.parameterRange; const t = selector.value;
    if (t < start || t > end) return unresolved('parameter-outside-domain');
    if (curve.kind === 'line') return point(a.start.map((v, i) => v + (a.end[i] - v) * (t - start) / (end - start)));
    if (circular) {
      const angle = a.xAxisAngleRad + (a.normalZ < 0 ? -t : t);
      return point([a.center[0] + a.radius * Math.cos(angle), a.center[1] + a.radius * Math.sin(angle)]);
    }
  }
  return unresolved('selector-not-supported-for-analytic-feature');
}
